"use client";

import { useEffect, useState } from "react";

import { fetchAuthorizedImageBlobUrl } from "@/lib/api-client";

export type AuthorizedImageState = "idle" | "loading" | "loaded" | "failed";

// Blob URLs are cached for the lifetime of the page rather than revoked on
// unmount: the same upload (a namespace icon, say) is rendered by several
// surfaces at once, so revoking when one of them unmounts would blank the
// copies still on screen.
const blobUrlCache = new Map<string, Promise<string>>();

function resolve(path: string): Promise<string> {
  let pending = blobUrlCache.get(path);
  if (!pending) {
    pending = fetchAuthorizedImageBlobUrl(path).catch((error: unknown) => {
      blobUrlCache.delete(path);
      throw error;
    });
    blobUrlCache.set(path, pending);
  }
  return pending;
}

// Only agent-API paths carry the bearer token. Absolute URLs belong to someone
// else, and a bare static asset like /avatar.png is served by Next directly —
// pushing either through the API client would break it.
function needsAuthorization(path: string): boolean {
  return path.startsWith("/api/");
}

/**
 * Turns a namespace-scoped upload path into something an `<img src>` can use.
 * Those endpoints need a bearer token, which a plain `<img>` can't send, so the
 * bytes are fetched through the API client and handed over as an object URL.
 * Anything else is passed straight through.
 */
export function useAuthorizedImageUrl(path: string | null | undefined): {
  url: string | null;
  state: AuthorizedImageState;
} {
  const isPassthrough = Boolean(path && !needsAuthorization(path));
  // Keyed by the path it was fetched for, so a path change reads as "loading"
  // without an extra state write inside the effect.
  const [entry, setEntry] = useState<{
    path: string;
    url: string | null;
    state: "loaded" | "failed";
  } | null>(null);

  useEffect(() => {
    if (!path || isPassthrough) {
      return;
    }

    let cancelled = false;
    resolve(path)
      .then((url) => {
        if (!cancelled) setEntry({ path, url, state: "loaded" });
      })
      .catch(() => {
        if (!cancelled) setEntry({ path, url: null, state: "failed" });
      });

    return () => {
      cancelled = true;
    };
  }, [path, isPassthrough]);

  if (isPassthrough) {
    return { url: path ?? null, state: "loaded" };
  }
  if (!path) {
    return { url: null, state: "idle" };
  }
  if (entry?.path === path) {
    return { url: entry.url, state: entry.state };
  }
  return { url: null, state: "loading" };
}
